import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { CollapseChevron } from "components/CollapseChevron";
import { forwardRef } from "react";

/**
 * Fixed height of the sticky section title bar. The tables inside sections
 * offset their own sticky column headers by this much so the two stack
 * instead of overlapping.
 */
export const SECTION_TITLE_HEADER_HEIGHT = "2.75rem";

export interface DiscrepancySectionProps {
  title: string;
  count: number;
  /** While loading the count isn't known yet — render children so their own skeletons show. */
  loading?: boolean;
  /** Shows a small red dot next to the title — used for time-critical items. */
  hasUrgent?: boolean;
  /** Hides the body while keeping the header (title + count) visible. Defaults to expanded. */
  collapsed?: boolean;
  /** Required when the header should be clickable to toggle `collapsed`. */
  onToggleCollapse?: () => void;
  /**
   * Shown as a "N selected" chip on the header whenever it's collapsed —
   * selecting rows, then collapsing the section, shouldn't make the
   * selection invisible. Omit for sections with no row selection.
   */
  selectedCount?: number;
  /**
   * Overrides the default "Nothing to review here." placeholder — e.g. to
   * distinguish "no items match the current status filter" from "there's
   * nothing here at all."
   */
  emptyMessage?: string;
  children: React.ReactNode;
}

/**
 * A top-level section in the Discrepancy Review panel, framed as a pale-gray
 * card (the SectionCard treatment from the manifest drawers) so stacked
 * sections read as distinct blocks. The header (title + count) always
 * renders, even at 0 — whether the section itself is shown at all is the
 * caller's call (the summary tiles). The header is a slightly stronger tinted
 * band that sticks to the top of the scroll container while its section is in
 * view, and is clickable to collapse/expand the body. When there's nothing to
 * show, a placeholder replaces the children so an empty section doesn't leave
 * a table with headers and no rows — and the chevron shows the same "closed"
 * orientation as a manually-collapsed section, since neither has an expanded
 * body to look at.
 */
export const DiscrepancySection = forwardRef<HTMLDivElement, DiscrepancySectionProps>(
  function DiscrepancySection(
    {
      title,
      count,
      loading = false,
      hasUrgent = false,
      collapsed = false,
      onToggleCollapse,
      selectedCount,
      emptyMessage = "Nothing to review here.",
      children,
    },
    ref
  ) {
    const isEmpty = !loading && count === 0;
    const looksClosed = collapsed || isEmpty;

    let body: React.ReactNode = children;
    if (collapsed) {
      body = null;
    } else if (isEmpty) {
      body = (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 2 }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <Paper
        ref={ref}
        variant="outlined"
        sx={{
          mx: 2,
          mb: 2,
          borderRadius: 2,
          // Grow with wide (horizontally scrolling) tables; never narrower
          // than the panel (minus the mx gutters above).
          width: "fit-content",
          minWidth: "calc(100% - 32px)",
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? theme.palette.grey[50] : theme.palette.grey[900],
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          onClick={onToggleCollapse}
          sx={{
            minHeight: SECTION_TITLE_HEADER_HEIGHT,
            position: "sticky",
            top: 0,
            // Above the tables' sticky column headers (MUI stickyHeader
            // cells are z-index 2) and facility group rows (1).
            zIndex: 3,
            bgcolor: (theme) =>
              theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[800],
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
            // Square while a body follows; rounded when the header is the whole card.
            borderBottomLeftRadius: collapsed ? "inherit" : 0,
            borderBottomRightRadius: collapsed ? "inherit" : 0,
            ...(onToggleCollapse
              ? {
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "light"
                        ? theme.palette.grey[200]
                        : theme.palette.grey[700],
                  },
                }
              : {}),
          }}
        >
          {/* Inner sticky wrapper pins the title to the left edge during horizontal scroll. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ position: "sticky", left: 0, px: 2 }}
          >
            {onToggleCollapse && <CollapseChevron collapsed={looksClosed} />}
            {hasUrgent && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "error.dark",
                  flexShrink: 0,
                }}
              />
            )}
            {/* Same overline treatment as the manifest drawer's SectionHeader;
                component=h6 keeps the heading role the overline span would lose. */}
            <Typography variant="overline" color="text.secondary" component="h6">
              {title}
            </Typography>
            <Chip size="small" label={count} sx={{ height: 20, fontSize: "0.75rem" }} />
            {collapsed && !!selectedCount && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${selectedCount} selected`}
                sx={{ height: 20, fontSize: "0.75rem" }}
              />
            )}
          </Stack>
        </Stack>
        {body}
      </Paper>
    );
  }
);
